/**
 * Runtime feature switches this deployment asks the viewers it embeds to offer.
 *
 * The develop and release webpages share one OBserver, and that OBserver resolves every viewer
 * version from a single published manifest — so both deployments always load the very same viewer
 * bundle, and nothing inside it can tell them apart. The shell is the only thing that differs, which
 * is why a feature is declared here at runtime, as the `features` field of the `getApiUrl` reply.
 * On the viewer side `ConnectionManager.hasFeature()` (web-libraries) answers from it, and
 * `ViewerActionsController` gates the actions side panel on `viewer-actions-sidebar`.
 *
 * The list is environment configuration (`REACT_APP_VIEWER_FEATURES` in `environments/.*.env`)
 * rather than code: enabling a feature on develop is then an env line, and release stays clean by
 * simply not setting the variable.
 *
 * Declaring a feature only *offers* it in the UI. Everything a viewer action actually does still
 * goes through the OBserver's `run` resource, which authorizes server-side — so this is not an
 * access control boundary and must never be used as one.
 */

/**
 * Read lazily rather than captured at module load so tests can point it somewhere. CRA's
 * DefinePlugin substitutes the literal wherever the member expression appears, so this still
 * inlines in a build. Same reasoning as `viewerManifest.ts`'s `manifestUrl()`.
 */
function configuredFeatures(): string | undefined {
  return process.env.REACT_APP_VIEWER_FEATURES
}

/**
 * The features this deployment declares, in the order they were configured.
 *
 * Tolerant of how the variable is written — blank entries and stray whitespace are dropped and
 * repeats collapse — because an env file edited by hand is exactly where a trailing comma ends up.
 */
export function viewerFeatures(): string[] {
  const configured = configuredFeatures()
  if (!configured) {
    return []
  }

  const features = new Set<string>()
  for (const entry of configured.split(',')) {
    const feature = entry.trim()
    if (feature) {
      features.add(feature)
    }
  }

  return Array.from(features)
}

/**
 * The `features` field of a `getApiUrl` reply, ready to spread into the payload.
 *
 * Absent rather than empty when this deployment declares nothing, so the release wire stays exactly
 * what it was before features existed: a viewer build that predates them sees no unknown field, and
 * one that knows them reads no feature either way.
 */
export function viewerFeaturesField(): {features?: string[]} {
  const features = viewerFeatures()

  return features.length > 0 ? {features} : {}
}
